import React, { useCallback, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Filament } from './Filament';
import { OrbitSlot } from './OrbitSlot';
import { StageCardView } from '../StageCardView';
import { usePointerParallax } from '../../../hooks/usePointerParallax';
import { useMotionPreference } from '../../../hooks/useMotionPreference';
import { cardAgents } from '../../../data/cardAgents';
import { getAgent } from '../../../utils/agents';
import { layoutOrbit, type WallGeometry } from '../../../utils/orbitLayout';
import type { AgentId, SoundId } from '../../../types/anna';
import type { StageCard } from '../../../types/stage';
interface OrbitCardsProps {
  g: WallGeometry;
  cards: StageCard[];
  onRemove: (uid: string) => void;
  onApproved: (agent: AgentId) => void;
  onSound: (id: SoundId) => void;
}
const DEFAULT_H = 260;

/** Summoned cards in orbit around Anna, each tied to her core by a filament. The agenda is drawn as an arc instead. */
export function OrbitCards({
  g,
  cards,
  onRemove,
  onApproved,
  onSound
}: OrbitCardsProps) {
  const reduce = useMotionPreference();
  const [heights, setHeights] = useState<Record<string, number>>({});
  const [pings, setPings] = useState<Record<string, number>>({});
  const orbiting = useMemo(() => cards.filter(c => c.kind !== 'vandaag'), [cards]);
  const layout = layoutOrbit(orbiting, heights, g);
  const parallax = usePointerParallax(reduce ? 0 : 7);
  const measure = useCallback((uid: string, h: number) => setHeights(prev => prev[uid] === h ? prev : {
    ...prev,
    [uid]: h
  }), []);
  const ping = useCallback((uid: string) => setPings(prev => ({
    ...prev,
    [uid]: (prev[uid] ?? 0) + 1
  })), []);
  return <motion.div className="pointer-events-none absolute inset-0 z-10" style={{
    x: parallax.x,
    y: parallax.y
  }} data-id="element-1474" data-mp="24s0k0xo1zirl">
      <svg className="absolute inset-0 h-full w-full overflow-visible" aria-hidden data-id="element-1475" data-mp="1h266seztns6t">
        <AnimatePresence data-id="element-1476" data-mp="1c3biixj35bfb">
          {orbiting.map(c => {
          const p = layout[c.uid];
          return <Filament key={c.uid} id={c.uid} x1={g.cx} y1={g.cy} x2={g.cx + p.anchorX} y2={g.cy + p.y} color={getAgent(cardAgents[c.kind]).color} ping={pings[c.uid] ?? 0} data-id="element-1477" data-mp="21s0c30nr6w1u" />;
        })}
        </AnimatePresence>
      </svg>
      <div className="absolute inset-0" style={{
      perspective: 1800
    }} data-id="element-1478" data-mp="1iupgbq1bykr5">
        <AnimatePresence data-id="element-1479" data-mp="1muptz8glurx5">
          {orbiting.map(c => <OrbitSlot key={c.uid} uid={c.uid} p={layout[c.uid]} g={g} h={heights[c.uid] ?? DEFAULT_H} reduce={reduce} onMeasure={measure} onPing={ping} data-id="element-1480" data-mp="1mxc156zl35eu">
              <StageCardView card={c} onRemove={onRemove} onApproved={onApproved} onSound={onSound} data-id="element-1481" data-mp="1wtp2mr1denk5" />
            </OrbitSlot>)}
        </AnimatePresence>
      </div>
    </motion.div>;
}